import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  BookOpen, Zap, Star, FileText, HelpCircle, DollarSign, ListOrdered,
  Plug, Shield, Users, Settings, Mail, ExternalLink, Loader2, ArrowRight,
  Globe, CheckCircle, Eye, TrendingUp, Check,
} from 'lucide-react';
import clsx from 'clsx';

const API = process.env.REACT_APP_API_URL || '';
const MARKETING_URL = process.env.REACT_APP_MARKETING_URL || 'https://rostery.com.au';

const QUICK_LINKS = [
  { icon: BookOpen,    label: 'Blog Posts',         desc: 'Articles & news',         path: '/website/blog',        color: 'from-violet-500 to-purple-600' },
  { icon: Zap,         label: 'Features',            desc: 'Product feature cards',   path: '/website/features',    color: 'from-teal-500 to-cyan-600' },
  { icon: Star,        label: 'Testimonials',        desc: 'Customer reviews',        path: '/website/testimonials',color: 'from-amber-500 to-orange-500' },
  { icon: HelpCircle,  label: 'FAQs',               desc: 'Frequently asked Q&A',    path: '/website/faqs',        color: 'from-blue-500 to-indigo-600' },
  { icon: DollarSign,  label: 'Pricing Plans',      desc: 'Plans & packages',        path: '/website/pricing',     color: 'from-emerald-500 to-green-600' },
  { icon: FileText,    label: 'Pages',              desc: 'Static content pages',    path: '/website/pages',       color: 'from-slate-500 to-slate-700' },
  { icon: ListOrdered, label: 'How It Works',       desc: 'Step-by-step flow',       path: '/website/how-it-works',color: 'from-pink-500 to-rose-600' },
  { icon: Plug,        label: 'Integrations',       desc: 'Third-party connections', path: '/website/integrations',color: 'from-orange-500 to-amber-600' },
  { icon: Shield,      label: 'Compliance Badges',  desc: 'Trust & cert badges',     path: '/website/compliance',  color: 'from-sky-500 to-blue-600' },
  { icon: Users,       label: 'Social Proof',       desc: 'Stats & logos',           path: '/website/social-proof',color: 'from-indigo-500 to-violet-600' },
  { icon: Settings,    label: 'Site Settings',      desc: 'Global config & SEO',     path: '/website/settings',    color: 'from-gray-500 to-slate-600' },
  { icon: Mail,        label: 'Contact Messages',   desc: 'Inbound enquiries',       path: '/website/contacts',    color: 'from-red-500 to-rose-600' },
];

function StatCard({ icon: Icon, label, value, color, loading, unread }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-center gap-4 hover:shadow-md transition-shadow">
      <div className={clsx('w-12 h-12 rounded-xl flex items-center justify-center shrink-0', color)}>
        <Icon size={22} className="text-white" />
      </div>
      <div>
        {loading ? (
          <div className="h-7 w-12 bg-slate-100 rounded-lg animate-pulse mb-1" />
        ) : (
          <p className="text-2xl font-bold text-slate-900">{value ?? '—'}</p>
        )}
        <p className="text-sm text-slate-500">{label}</p>
        {!loading && unread > 0 && (
          <p className="text-xs text-red-500 font-semibold mt-0.5">{unread} unread</p>
        )}
      </div>
    </div>
  );
}

function QuickLinkCard({ icon: Icon, label, desc, path, color, onClick }) {
  return (
    <button
      onClick={() => onClick(path)}
      className="group flex items-center gap-3 p-3.5 bg-white rounded-xl border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all text-left w-full"
    >
      <div className={clsx('w-9 h-9 rounded-lg bg-gradient-to-br flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform', color)}>
        <Icon size={16} className="text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-slate-800 group-hover:text-slate-900 truncate">{label}</p>
        <p className="text-xs text-slate-400 truncate">{desc}</p>
      </div>
      <ArrowRight size={14} className="text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5 transition-all shrink-0" />
    </button>
  );
}

function formatDate(str) {
  if (!str) return '';
  const d = new Date(str);
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
}

function initials(name) {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

const AVATAR_COLORS = [
  'from-violet-500 to-purple-600',
  'from-teal-500 to-cyan-600',
  'from-amber-500 to-orange-500',
  'from-blue-500 to-indigo-600',
  'from-emerald-500 to-green-600',
];

export default function MarketingOverview() {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [contacts, setContacts] = useState([]);
  const [contactsLoading, setContactsLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    async function loadStats() {
      setStatsLoading(true);
      try {
        const res = await fetch(`${API}/api/marketing/dashboard-stats`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setStats(data);
        }
      } catch {
        // silently fail — stats just stay null
      } finally {
        setStatsLoading(false);
      }
    }

    async function loadContacts() {
      setContactsLoading(true);
      try {
        const res = await fetch(`${API}/api/marketing/contact-messages?limit=5`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          if (data && typeof data === 'object' && !Array.isArray(data) && Array.isArray(data.messages)) {
            setContacts(data.messages.slice(0, 5));
            setUnreadCount(data.unread_count ?? 0);
          } else {
            setContacts(Array.isArray(data) ? data.slice(0, 5) : []);
          }
        }
      } catch {
        // silently fail
      } finally {
        setContactsLoading(false);
      }
    }

    loadStats();
    loadContacts();
  }, []);

  async function markAsRead(msgId) {
    try {
      const res = await fetch(`${API}/api/marketing/contact-messages/${msgId}/read`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setContacts(prev => prev.map(m => m.id === msgId ? { ...m, is_read: true } : m));
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch {
      toast.error('Failed to mark as read');
    }
  }

  const statCards = [
    { icon: BookOpen,  label: 'Blog Posts',       value: stats?.posts?.total,        color: 'bg-gradient-to-br from-violet-500 to-purple-600' },
    { icon: Zap,       label: 'Features',          value: stats?.features?.total,     color: 'bg-gradient-to-br from-teal-500 to-cyan-600' },
    { icon: Star,      label: 'Testimonials',      value: stats?.testimonials?.total, color: 'bg-gradient-to-br from-amber-500 to-orange-500' },
    { icon: FileText,  label: 'Pages',             value: stats?.pages?.total,        color: 'bg-gradient-to-br from-blue-500 to-indigo-600' },
    { icon: Mail,      label: 'Contact Messages',  value: stats?.contacts?.total,     color: 'bg-gradient-to-br from-red-500 to-rose-600', unread: unreadCount },
  ];

  const statusItems = [
    { label: 'Blog Posts',   value: stats?.posts?.published,        total: stats?.posts?.total,        color: 'bg-violet-500' },
    { label: 'Features',     value: stats?.features?.active,        total: stats?.features?.total,     color: 'bg-teal-500' },
    { label: 'Testimonials', value: stats?.testimonials?.featured,  total: stats?.testimonials?.total, color: 'bg-amber-500' },
    { label: 'FAQs',         value: stats?.faqs?.total,             total: stats?.faqs?.total,         color: 'bg-blue-500' },
    { label: 'Pages',        value: stats?.pages?.active,           total: stats?.pages?.total,        color: 'bg-emerald-500' },
  ];

  return (
    <div className="p-6 space-y-6 max-w-screen-xl">

      {/* ── Page Header ── */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Marketing Site</h1>
          <p className="text-slate-500 text-sm mt-0.5">Manage all content published to your ShiftCore marketing website</p>
        </div>
        <a
          href={MARKETING_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-2 px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-semibold hover:border-slate-300 hover:shadow-sm transition-all"
        >
          <Globe size={15} className="text-teal-500" />
          Visit Site
          <ExternalLink size={13} className="text-slate-400" />
        </a>
      </div>

      {/* ── Stat Cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        {statCards.map(s => (
          <StatCard key={s.label} {...s} loading={statsLoading} />
        ))}
      </div>

      {/* ── Main 2-col Row ── */}
      <div className="grid grid-cols-1 xl:grid-cols-5 gap-6">

        {/* Quick Access — 3 cols */}
        <div className="xl:col-span-3 bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center">
              <TrendingUp size={14} className="text-white" />
            </div>
            <h2 className="text-base font-bold text-slate-900">Quick Access</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {QUICK_LINKS.map(link => (
              <QuickLinkCard key={link.path} {...link} onClick={navigate} />
            ))}
          </div>
        </div>

        {/* Recent Contact Messages — 2 cols */}
        <div className="xl:col-span-2 bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex flex-col">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center">
                <Mail size={14} className="text-white" />
              </div>
              <h2 className="text-base font-bold text-slate-900">Contact Messages</h2>
              {unreadCount > 0 && (
                <span className="px-2 py-0.5 text-xs font-bold bg-red-500 text-white rounded-full">{unreadCount}</span>
              )}
            </div>
            <button
              onClick={() => navigate('/website/contacts')}
              className="text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors"
            >
              View all →
            </button>
          </div>

          {contactsLoading ? (
            <div className="flex-1 flex items-center justify-center py-8">
              <Loader2 size={24} className="animate-spin text-slate-300" />
            </div>
          ) : contacts.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center py-10 text-slate-400">
              <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <Mail size={24} className="opacity-40" />
              </div>
              <p className="text-sm font-medium">No messages yet</p>
              <p className="text-xs mt-1 text-slate-300">Contact form submissions will appear here</p>
            </div>
          ) : (
            <div className="flex-1 space-y-1 -mx-1">
              {contacts.map((msg, idx) => (
                <div
                  key={msg.id || idx}
                  className="flex items-start gap-2 px-3 py-2.5 rounded-xl hover:bg-slate-50 transition-colors group"
                >
                  {/* Unread dot */}
                  <div className={clsx(
                    'w-2 h-2 rounded-full shrink-0 mt-2.5',
                    msg.is_read ? 'bg-transparent' : 'bg-blue-500'
                  )} />
                  <div className={clsx(
                    'w-8 h-8 rounded-full bg-gradient-to-br flex items-center justify-center text-white text-xs font-bold shrink-0 mt-0.5',
                    AVATAR_COLORS[idx % AVATAR_COLORS.length]
                  )}>
                    {initials(msg.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-semibold text-slate-800 truncate">{msg.name || 'Unknown'}</p>
                      <div className="flex items-center gap-1 shrink-0">
                        <span className="text-[11px] text-slate-400">{formatDate(msg.created_at)}</span>
                        {!msg.is_read && (
                          <button
                            onClick={() => markAsRead(msg.id)}
                            title="Mark as read"
                            className="opacity-0 group-hover:opacity-100 p-1 rounded text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all"
                          >
                            <Check size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 truncate">{msg.email}</p>
                    {msg.message && (
                      <p className="text-xs text-slate-400 mt-0.5 line-clamp-1">{msg.message}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Status Strip ── */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
        <div className="flex items-center gap-2 mb-4">
          <CheckCircle size={16} className="text-emerald-500" />
          <h2 className="text-sm font-bold text-slate-900">Published Content Status</h2>
          {statsLoading && <Loader2 size={13} className="animate-spin text-slate-300 ml-1" />}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {statusItems.map(item => {
            const pct = item.total > 0 ? Math.round((item.value / item.total) * 100) : 0;
            return (
              <div key={item.label}>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs font-medium text-slate-600">{item.label}</span>
                  <span className="text-xs text-slate-400">
                    {statsLoading ? '…' : `${item.value ?? 0}/${item.total ?? 0}`}
                  </span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className={clsx('h-full rounded-full transition-all duration-500', item.color)}
                    style={{ width: statsLoading ? '0%' : `${pct}%` }}
                  />
                </div>
                <p className="text-[11px] text-slate-400 mt-1">{statsLoading ? '' : `${pct}% active`}</p>
              </div>
            );
          })}
        </div>
      </div>

    </div>
  );
}
